class FacilityEditFormController {

    constructor(
        FacilityEditFormService,
        FacilitySettings,
        CommonRequest,
        CurrentUser
    ){
        'ngInject';

        this.FacilityEditFormService = FacilityEditFormService;
        this.FacilitySettings = FacilitySettings;
        this.CommonRequest = CommonRequest;
        this.CurrentUser = CurrentUser;
    }

    $onInit() {

        this.isErrorVisible = false;
        this.errorMessage = '';
        this.showSaveSuccess = false;

        // extract the selected campus and primary markets
        this.selectedCampus = this.getListItem(this.campusList, this.facility.campus);
        this.selectedPrimaryMarket = this.getListItem(this.facility.markets, this.facility.primary_market);

        // extract sku market preferences from facility so that we can detect changes later
        // also, initialize the visibility of sku categories
        this.skuMarketPreferences = {};
        this.etpCategoryVisibility = {};
        for (let [category_id, category] of Object.entries(this.facility.sku_data)) {
            this.etpCategoryVisibility[category_id] = false;
            for (let [sku_id, sku] of Object.entries(category['skus'])) {
                this.skuMarketPreferences[sku_id] = null;
                for (let [market_id, market] of Object.entries(sku['market_prices'])) {
                    if (market['selected']) {
                        this.skuMarketPreferences[sku_id] = market_id;
                    }
                }
            }
        }

        this.setMonthSelections();

        // Only these fields will be editable on the form. All other fields will be display-only.
        this.editableFields = this.FacilityEditFormService.getEditableFields();

        // The properties of each setting that is used to display the selected value. This is necessary for setting up
        // the dropdown selectors.
        this.displayProps = this.FacilitySettings.displayProps;

        // An object where the key is a FacilitySetting name and the value is the corresponding setting's details.
        this.facilityFieldMap = this.FacilitySettings.facilityFieldMap;

        this.phonePattern = this.FacilitySettings.phonePattern;

        // Set the localization settings based upon the facility's country and set them locally
        this.FacilitySettings.setLocalizationUnits(this.facility);
        this.units = this.FacilitySettings.units;

        this.facility.practitioners = this.configurePractitioners();

        // configure our selected ETP start and ends
        // explicitly specify year, month, and date so that it doesn't automatically adjust the date for local tz
        this.selectedEtpStart = null;
        this.selectedEtpEnd = null;
        if (this.facility.etp_start) {
            let etpStartParts = this.getDateParts(this.facility.etp_start);
            this.selectedEtpStart = new Date(etpStartParts['year'], etpStartParts['month'], etpStartParts['day']);
        }

        if (this.facility.etp_end) {
            let etpEndParts = this.getDateParts(this.facility.etp_end);
            this.selectedEtpEnd = new Date(etpEndParts['year'], etpEndParts['month'], etpEndParts['day']);
        }

        // fairly arbitrary minimum date for some of our date pickers
        this.minDate = new Date(2010, 1, 1);

        this.configureSettings();
    }

    /**
     * Simple function to toggle sku category visibility on/off in the template
     * @param category_id
     */
    toggleEtpCategoryVisibility(category_id) {
        this.etpCategoryVisibility[category_id] = !this.etpCategoryVisibility[category_id];
    }

    /***
     * gets a specific item from an array of items based on item id
     * @param itemId: the id of the item being sought
     * @returns the item object object
     */
    getListItem(itemlist, itemId) {
       return itemlist.filter((c) => c.id === itemId)[0];
    }

    /***
     * Sets the currently selected campus to a specific campus
     * @param campus The campus that will now be active
     */
    setCampus(campus) {
        this.selectedCampus = campus;
    }

    /**
     * Sets the current primary MBO market and also switches the sku+market preferences to that of the chosen market
     * @param market object
     */
    setPrimaryMarket(market) {
        this.selectedPrimaryMarket = market;
        for (const [sku_id, market_id] of Object.entries(this.skuMarketPreferences)) {
            this.skuMarketPreferences[sku_id] = String(market['id']);
        }
    }

    setMonthSelections() {

        const currentDate = new Date();
        this.month = currentDate.getMonth() + 1;
        this.year = currentDate.getFullYear();

        this.openingMonth = 13;
        this.openingYear = 'None Set';
        if (this.facility.opening_date) {
            const openingDateParts = this.getMonthYear(this.facility.opening_date);
            this.openingMonth = openingDateParts['month'];
            this.openingYear = openingDateParts['year'];
        }

        this.closingMonth = 13;
        this.closingYear = 'None Set';
        if (this.facility.closing_date) {
            const closingDateParts = this.getMonthYear(this.facility.closing_date);
            this.closingMonth = closingDateParts['month'];
            this.closingYear = closingDateParts['year'];
        }

    }

    getMonthYear(dateAsString) {
        const dateParts = dateAsString.split('-');
        return {'year': parseInt(dateParts[0]), 'month': parseInt(dateParts[1])}
    }

    /**
     * breaks apart a string date and returns it's parts
     *
     * @param dateAsString
     * @returns {{month: number, year: number, day: number}}
     */
    getDateParts(dateAsString) {
        const dateParts = dateAsString.split('-');
        return {'year': parseInt(dateParts[0]), 'month': parseInt(dateParts[1]), 'day': parseInt(dateParts[2])}
    }

    /***
     * Sets up some additional parameters that are specific to the facility that is being displayed. Includes:
     * 1) setting which value is currently saved for each of the related dropdown selectors
     * 2) translates the boolean list of Days of Operation into a list of Days object IDs
     * 3) populates all of the checkbox fields to check the values currently saved in the database
     * 4) populates the Days of Operation list of Days to check the values currently saved in the database
     */
    configureSettings() {
        // create a copy of some settings so that we may be able to detect changes when saving
        this.previousFacility = angular.copy(this.facility);
        this.previousSkuMarketPreferences = angular.copy(this.skuMarketPreferences);
        this.selectedRelatedModels = this.FacilitySettings.populateSelectedRelatedModels(this.facilityFields,
                                                                                         this.relatedModels,
                                                                                         this.facilitySettings);

        this.FacilitySettings.populateSelectedCheckboxItems(this.facilityFields,
                                                            this.relatedModels,
                                                            this.facilitySettings);

        this.previousSettings = angular.copy(this.facilitySettings);
    }

    /***
     * Converts all of the PractitionerFacility objects attached to this Facility and returns the list of
     * corresponding Practitioners. Each Practitioner also has an isRemoved property because that is an expected
     * property of the RelatedListField that displays them.
     *
     * @returns {array}
     */
    configurePractitioners() {
        return this.facility.practitioner_facilities.map((pf) => {
            let practitioner = pf.practitioner;
            practitioner.isRemoved = !pf.is_assigned;
            return practitioner;
        });
    }

    /***
     * Callback function that is triggered whenever a dropdown option is selected.
     *
     * @param field     the "name" property of the Facility Setting that is being selected.
     * @param item      an object representing the item that was selected.
     */
    selectDropdownOption(field, item) {
        this.selectedRelatedModels[field] = item;
    }

    /***
     * Callback function for the MonthNavigator component. When a month or year is changed, the settings are
     * updated and then reconfigured.
     *
     * @param month     the new month of settings being fetched
     * @param year      the new year of settings being fetched
     */
    onDateChange(month, year) {
        this.month = month;
        this.year = year;
        this.FacilityEditFormService.getFacilitySettings(this.facility.id, this.month, this.year)
            .then((successResponse) => {
                this.facilitySettings = successResponse;
                this.configureSettings();
            })
    }

    onOpeningDateChange(month, year) {
        this.openingMonth = month;
        this.openingYear = year;
    }

    onClosingDateChange(month, year) {
        this.closingMonth = month;
        this.closingYear = year;
    }

    /**
     * Takes a Date object, returns a YYYY-MM-DD formatted date string
     *
     * @param dateObj       Date object. TZ is irrelevant here.
     * @returns {string}    YYYY-MM-DD formatted string. won't have any TZ adjustments
     */
    formatDateToString(dateObj) {
        let y = dateObj.getFullYear().toString();
        let m = (dateObj.getMonth() + 1).toString(); // getMonth() is zero-based
        let d  = dateObj.getDate().toString();
        return y + '-' + (m[1] ? m : "0" + m[0]) + '-' + (d[1] ? d : "0" + d[0]);
    }

    /**
     * Takes the Date object from the form, converts it to YYYY-MM-DD and assigns it to this.facility.etp_start
     *
     * @param startDate     a Date object that comes in from the form
     */
    onEtpStartDateChange(startDate) {
        // format to YYYY-MM-DD so we can deliver this the same way we received it
        if (startDate) {
            this.facility.etp_start = this.formatDateToString(startDate);
        } else {
            this.facility.etp_start = null;
        }
    }

    /**
     * Takes the Date object from the form, converts it to YYYY-MM-DD and assigns it to this.facility.etp_end
     *
     * @param startDate     a Date object that comes in from the form
     */
    onEtpEndDateChange(endDate) {
        // format to YYYY-MM-DD so we can deliver this the same way we received it
        if (endDate) {
            this.facility.etp_end = this.formatDateToString(endDate);
        } else {
            this.facility.etp_end = null;
        }
    }

    hideError() {
        this.isErrorVisible = false;
        this.errorMessage = '';
    }

    /***
     * Fetches a PractitionerFacility object attached to this Facility by the Practitioner's ID.
     *
     * @param practitionerId        the ID of the Practitioner being fetched
     *
     * @returns {object}
     */
    getPractitionerFacilityById(practitionerId) {
        return this.facility.practitioner_facilities.filter((pf) => pf.practitioner.id === practitionerId).pop();
    }

    /***
     * Creates a new Practitioner-Facility assignment for the current Facility and the given practitioner ID.
     *
     * @param practitionerId        the ID of the practitioner being assigned to this facility
     *
     * @returns {Promise}
     */
    addPractitioner(practitionerId) {
        return this.FacilityEditFormService.addPractitionerToFacility(this.facility.id, practitionerId)
            .then((successResponse) => {
            }, (errorResponse) => {
                this.errorMessage = errorResponse.data[0];
                this.isErrorVisible = true;
            });
    }

    /***
     * Updates a Practitioner-Facility assignment to toggle whether the given practitioner is assigned to
     * this facility.
     *
     * @param practitioner      a Practitioner object
     *
     * @returns {Promise}
     */
    togglePractitionerAssignment(practitioner) {
        const practitionerFacility = this.getPractitionerFacilityById(practitioner.id);
        if (practitionerFacility !== undefined) {
            const newStatus = practitionerFacility.is_assigned === 1 ? 0 : 1;
            const data = {
                is_assigned: newStatus
            };

            const newStatusVerb = newStatus === 1 ? 'reassign' : 'remove';
            const newStatusPreposition = newStatus === 1 ? 'to' : 'from';
            const doToggle = confirm(`Are you sure you want to ${newStatusVerb} `
                                      + `${practitionerFacility.practitioner.name} ${newStatusPreposition} `
                                      + `${this.facility.name}?`);
            if (doToggle) {
                return this.CommonRequest.updatePractitionerFacilityAssignment(practitionerFacility.id, data)
                    .then((successResponse) => {
                        practitionerFacility.is_assigned = successResponse.is_assigned;
                    });
            }
        }
    }

    /***
     * A single callback function to update a Practitioner-Facility assignment. If the item is already present in
     * the list of practitioners assigned to this Facility, then we just update the existing PractitionerFacility
     * instance. Otherwise, it creates a new PractitionerFacility instance to map the two.
     *
     * @param item          an object representing the practitioner either being added or toggled
     * @param itemIds       a list of practitioner IDs currently assigned to this facility
     *
     * @returns {Promise}
     */
    updateFacilityPractitioners(item, itemIds) {
        const facilityPractiterIds = this.facility.practitioner_facilities.map((pf) => pf.practitioner.id);
        if (facilityPractiterIds.includes(item.id)) {
            return this.togglePractitionerAssignment(item);
        }
        else {
            return this.addPractitioner(item.id);
        }

    }

    /***
     * Checks whether any FacilitySettings have been changed. If so, the new values are passed up to the API to commit
     * them to the database.
     *
     * Returns early if there is nothing to update.
     */
    saveFacilitySettings() {

        this.isErrorVisible = false;
        this.errorMessage = '';

        this.showSaveSuccess = false;
        const settingsToSave = this.FacilitySettings.getSettingsToSave(this.facilityFields,
                                                                       this.facilitySettings,
                                                                       this.previousSettings,
                                                                       this.selectedRelatedModels);

        let openingDate = null;
        let openingDateString = null;
        if (this.openingYear !== 'None Set' && this.openingMonth !== 13) {
            openingDate = new Date(Date.UTC(this.openingYear, this.openingMonth - 1, 1));
            openingDateString = openingDate.toISOString().slice(0, 10);
        }

        let closingDate = null;
        let closingDateString = null;
        if (this.closingYear !== 'None Set' && this.closingMonth !== 13) {
            // the months sent are the 'real-life' months, so they're ahead by one month in
            // JS months, which is fine since we want to get the last day of the 'real-life' month,
            // which the 0 provides
            closingDate = new Date(Date.UTC(this.closingYear, this.closingMonth, 0));
            closingDateString = closingDate.toISOString().slice(0, 10);
        }

        if (closingDate && openingDate && closingDate < openingDate) {
            this.isErrorVisible = true;
            this.errorMessage = 'The First Month of Operation needs to be set before the Month of Closure.';
            return;
        }

        if (this.facility.mbo_studio_id && parseInt(this.facility.mbo_studio_id) != this.facility.mbo_studio_id) {
            // quick mbo studio id validation
            this.isErrorVisible = true;
            this.errorMessage = 'MBO Studio ID needs to be an integer. Can be negative.';
            return;
        }

        if (this.facility.mbo_studio_id && (this.facility.mbo_studio_id.replace('-', '').length > 10)) {
            // quick mbo studio id validation
            this.isErrorVisible = true;
            this.errorMessage = 'MBO Studio ID cannot be more than 10 characters.';
            return;
        }

        if (this.facility.etp_start && this.facility.etp_end && (this.facility.etp_start >= this.facility.etp_end)) {
            this.isErrorVisible = true;
            this.errorMessage = 'EXOS Training Program end date needs to come after start date.';
            return;
        }

        if (this.facility.etp_end && !this.facility.etp_start) {
            this.isErrorVisible = true;
            this.errorMessage = 'EXOS Training Program cannot end if it never started.';
            return;
        }

        let new_campus_id = this.selectedCampus.id;
        let new_primary_market_id = this.selectedPrimaryMarket ? this.selectedPrimaryMarket.id : undefined;

        // check if any facility settings or facility attributes have changed
        if (Object.keys(settingsToSave).length === 0
            && new_campus_id === this.previousFacility.campus
            && new_primary_market_id == this.previousFacility.primary_market
            && this.facility.name === this.previousFacility.name
            && this.facility.opening_date == openingDateString
            && this.facility.closing_date == closingDateString
            && this.facility.is_physical_therapy_available == this.previousFacility.is_physical_therapy_available
            && this.facility.mbo_studio_id == this.previousFacility.mbo_studio_id
            && this.facility.etp_start == this.previousFacility.etp_start
            && JSON.stringify(this.previousSkuMarketPreferences) == JSON.stringify(this.skuMarketPreferences)
            && this.facility.etp_start == this.previousFacility.etp_end) {
            // nothing has changed? return early
            return;
        }

        const data = {
            month: this.month,
            year: this.year,
            opening_date: openingDateString,
            closing_date: closingDateString,
            name: this.facility.name,
            campus_id: new_campus_id,
            is_physical_therapy_available: this.facility.is_physical_therapy_available,
            facility_setting_values: settingsToSave,
            etp_start: this.facility.etp_start ? this.facility.etp_start : null,
            etp_end: this.facility.etp_end ? this.facility.etp_end : null,
            mbo_studio_id: this.facility.mbo_studio_id ? this.facility.mbo_studio_id : null,
            primary_market: new_primary_market_id ? new_primary_market_id : null,
            sku_market_preferences: this.skuMarketPreferences
        };

        this.FacilityEditFormService.updateFacilitySettings(this.facility.id, data)
            .then((successResponse) => {
                this.previousSettings = angular.copy(this.facilitySettings);
                this.previousFacility.is_physical_therapy_available = this.facility.is_physical_therapy_available;
                if (this.previousFacility.name !== this.facility.name) {
                    this.previousFacility.name = this.facility.name;
                }
                this.previousFacility.campus = new_campus_id;

                this.updateTree();
                this.showSaveSuccess = true;
            }, (errorResponse) => {
                this.isErrorVisible = true;
                this.errorMessage = errorResponse.data;
            });
    }

}

export default FacilityEditFormController;



// WEBPACK FOOTER //
// ./app/components/client-edit/facility-edit-form/facility-edit-form-controller.js